import * as React from 'react';
import { Link } from 'react-router';
import {
  CameraIcon,
  MapPinIcon,
  SendIcon,
  SearchIcon,
  CheckCircle2Icon,
  ArrowRightIcon,
  MapIcon,
  SmartphoneIcon,
  ClipboardListIcon,
  EyeIcon,
  QrCodeIcon,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useGetCivicReportSummary, type ReportSummary } from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/layout/public-header';
import { PublicFooter } from '@/components/layout/public-footer';
import { PURPOSES } from '@/lib/purposes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { appIdentity } from '@/app/app-config';

/* ─── Impact stats from /api/civic/reports/summary ──────────────── */

function ImpactStats() {
  const { data, isError, isLoading } = useGetCivicReportSummary();

  if (isLoading) {
    return (
      <section className="border-y border-border bg-muted/40">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-6 px-4 py-8 sm:gap-10 md:gap-16">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (isError || !data?.statusCounts) return null;

  const counts = aggregateStats(data);
  if (counts.total === 0) return null;

  return (
    <section className="border-y border-border bg-muted/40">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-6 px-4 py-8 sm:gap-10 md:gap-16">
        <StatBlock value={counts.total} label="Reports filed" />
        <StatBlock value={counts.resolved} label="Issues resolved" />
        <StatBlock value={counts.active} label="In progress" />
      </div>
    </section>
  );
}

function aggregateStats(data: ReportSummary) {
  let total = 0;
  let resolved = 0;
  let active = 0;
  for (const item of data.statusCounts ?? []) {
    const c = item.count ?? 0;
    total += c;
    if (item.status === 'resolved' || item.status === 'closed') resolved += c;
    if (item.status === 'in_progress' || item.status === 'scheduled' || item.status === 'acknowledged') active += c;
  }
  return { total, resolved, active };
}

function StatBlock({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-3xl font-bold tracking-tight text-primary">{value.toLocaleString()}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

/* ─── How it works ──────────────────────────────────────────────── */

const STEPS = [
  { icon: CameraIcon, title: 'Record', description: 'Take a photo of the issue with your phone.' },
  { icon: SendIcon, title: 'Report', description: 'Pin the location and submit — no account needed.' },
  { icon: SearchIcon, title: 'Review', description: "Get a tracking link to follow your report\u2019s progress." },
  { icon: CheckCircle2Icon, title: 'Resolved', description: 'The responsible organisation fixes the issue and closes the loop.' },
] as const;

function HowItWorks() {
  return (
    <section className="mx-auto max-w-5xl px-4 py-16 md:py-20">
      <div className="mb-10 text-center">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">How it works</h2>
        <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
          Reporting an issue takes less than a minute.
        </p>
      </div>
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, idx) => (
          <div key={step.title} className="flex flex-col items-center text-center">
            <div className="relative mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
              <step.icon className="h-6 w-6" aria-hidden />
              <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {idx + 1}
              </span>
            </div>
            <h3 className="text-lg font-semibold">{step.title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Feature cards ─────────────────────────────────────────────── */

interface FeatureProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  to: string;
  cta: string;
}

const FEATURES: FeatureProps[] = [
  {
    icon: MapIcon,
    title: 'Map',
    description: 'Explore reported issues, asset boundaries, and physical infrastructure on an interactive map — from council streets to farm fences and workplace sites.',
    to: '/map?lat=-37.8136&lng=144.9631&zoom=12',
    cta: 'Open Map',
  },
  {
    icon: SmartphoneIcon,
    title: 'AR Asset View',
    description: 'Point your phone at nearby objects to identify them and instantly see product details, ownership, condition, and maintenance information in augmented reality.',
    to: '/ar',
    cta: 'Launch AR',
  },
  {
    icon: ClipboardListIcon,
    title: 'Track a Report',
    description: 'Already submitted a report? Enter your tracking code to check its current status and timeline.',
    to: '/track',
    cta: 'Track Report',
  },
];

function FeatureSection() {
  return (
    <section className="bg-muted/30">
      <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
        <div className="mb-10 text-center">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">More ways to get involved</h2>
          <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
            Beyond reporting, explore your surroundings and keep the responsible team accountable.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <Card key={f.title} className="group relative overflow-hidden transition-shadow hover:shadow-md">
              <CardContent className="flex flex-col gap-4 p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <f.icon className="h-5 w-5" aria-hidden />
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <h3 className="text-lg font-semibold">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{f.description}</p>
                </div>
                <Button asChild variant="outline" size="sm" className="w-fit">
                  <Link to={f.to}>
                    {f.cta}
                    <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Purpose teaser ────────────────────────────────────────────── */

function PurposeTeaser() {
  return (
    <section className="mx-auto max-w-5xl px-4 py-16 md:py-20">
      <div className="mb-10 text-center">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Who it&rsquo;s for</h2>
        <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
          Whatever you maintain, Fix It Foundry adapts to your workflow.
        </p>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PURPOSES.map((purpose) => (
          <Card key={purpose.slug} className="group relative overflow-hidden transition-shadow hover:shadow-md">
            <CardContent className="flex flex-col gap-4 p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <purpose.icon className="h-5 w-5" aria-hidden />
              </div>
              <div className="flex flex-1 flex-col gap-1.5">
                <h3 className="text-lg font-semibold">{purpose.shortName}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{purpose.tagline}</p>
              </div>
              <Button asChild variant="outline" size="sm" className="w-fit">
                <Link to={`/for/${purpose.slug}`}>
                  Learn more
                  <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* ─── QR Code dialog for desktop ────────────────────────────────── */

function PhoneHandoffDialog() {
  const reportUrl = React.useMemo(() => {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    return `${base}/report?purpose=councils`;
  }, []);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="lg" variant="outline" className="hidden text-base md:inline-flex">
          <QrCodeIcon className="mr-2 h-5 w-5" aria-hidden />
          Continue on your phone
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Report from your phone</DialogTitle>
          <DialogDescription>
            The report wizard works best on a phone — scan this QR code with your camera to jump straight in.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="rounded-xl border border-border bg-white p-4">
            <QRCodeSVG value={reportUrl} size={200} level="M" />
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Point your phone camera at the code — no app needed.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Hero ──────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative isolate overflow-hidden">
      {/* Gradient background treatment */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,var(--color-primary)/15,transparent)]"
      />
      <div
        aria-hidden
        className="absolute -left-40 -top-40 -z-10 h-[600px] w-[600px] rounded-full bg-primary/5 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-20 -right-32 -z-10 h-[400px] w-[400px] rounded-full bg-primary/5 blur-3xl"
      />

      <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-4 pb-8 pt-16 text-center sm:pt-20 md:pb-12 md:pt-28">
        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
          <EyeIcon className="h-3.5 w-3.5" aria-hidden />
          Report issues on any physical asset
        </div>

        <h1 className="max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">
          Report. Review.{' '}
          <span className="text-primary">Resolve.</span>
        </h1>

        <p className="max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
          Spot something broken on a council street, a farm fence, or a workplace site? Report it in under a minute — no account needed.
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="text-base">
            <Link to="/report?purpose=councils">
              <CameraIcon className="mr-2 h-5 w-5" aria-hidden />
            Report
          </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <Link to="/map?lat=-37.8136&lng=144.9631&zoom=12">
              <MapPinIcon className="mr-2 h-5 w-5" aria-hidden />
              Reporting Map
            </Link>
          </Button>
          <PhoneHandoffDialog />
        </div>
      </div>
    </section>
  );
}

/* ─── Main page ─────────────────────────────────────────────────── */

/** Asset & issue reporting platform home — primary CTA is reporting an issue. */
export function HomePage() {
  React.useEffect(() => {
    document.title = `${appIdentity.name} — Report issues on physical assets`;
  }, []);

  return (
    <div className="flex min-h-dvh flex-col">
      <PublicHeader />

      <main className="flex-1">
        <Hero />
        <ImpactStats />
        <HowItWorks />
        <FeatureSection />
        <PurposeTeaser />
      </main>

      <PublicFooter />

      {/* Sticky mobile Report CTA bar — visible on small viewports only */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-md sm:hidden">
        <Button asChild size="lg" className="w-full text-base">
          <Link to="/report?purpose=councils">
            <CameraIcon className="mr-2 h-5 w-5" aria-hidden />
            Report
          </Link>
        </Button>
      </div>

      {/* Bottom spacer so the fixed bar doesn't cover footer content on mobile */}
      <div className="h-[calc(72px+env(safe-area-inset-bottom))] sm:hidden" aria-hidden />
    </div>
  );
}
